<template>
  <ChartCard data-widget-id="race-distribution">
    <ChartHeader
      label="What is the racial distribution of our candidate pool?"
      widget-download-id="race-distribution"
      tooltip-key="demo-survey-insights-bar"
    />
    <div class="chart-container">
      <BarChart id="dsr-race-distribution" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </div>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed, ref } from 'vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { storeToRefs } from 'pinia';
import { useColors } from '@/composables/colors';
import { initTooltip } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/tooltip';
import { flatten } from 'lodash-es';
import { numberWithCommas } from '@/helpers/formatters';
import { datalabelsConfig } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/datalabelsConfig';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const { raceBreakdown } = storeToRefs(useDemographicSurveyReport());

const {
  asian,
  black,
  hispanic_latino: hispanicLatino,
  native_american: nativeAmerican,
  two_or_more: twoOrMore,
  white,
  pacific_islander: pacificIslander,
  declined,
  unknown,
} = raceBreakdown.value;

const {
  candidateNotSubmitted,
  candidateDeclined,
  candidateTwoOrMore,
  candidateHawaiian,
  candidateWhite,
  candidateHispanic,
  candidateAsian,
  candidateBlack,
  candidateNativeAm,
  textDarkest,
  white: whiteColor,
} = useColors();

const overallTotal = computed(() => black + twoOrMore + declined + unknown
    + nativeAmerican + asian + hispanicLatino + white + pacificIslander);

const blackVal = computed(() => ((black || 0) / overallTotal.value) * 100);
const twoOrMoreVal = computed(() => ((twoOrMore || 0) / overallTotal.value) * 100);
const declinedVal = computed(() => ((declined || 0) / overallTotal.value) * 100);
const unknownVal = computed(() => ((unknown || 0) / overallTotal.value) * 100);
const nativeAmericanVal = computed(() => ((nativeAmerican || 0) / overallTotal.value) * 100);
const asianVal = computed(() => ((asian || 0) / overallTotal.value) * 100);
const hispanicLatinoVal = computed(() => ((hispanicLatino || 0) / overallTotal.value) * 100);
const whiteVal = computed(() => ((white || 0) / overallTotal.value) * 100);
const pacificIslanderVal = computed(() => ((pacificIslander || 0) / overallTotal.value) * 100);

const sortArray = computed(() => [
  { key: 'black', value: blackVal.value },
  { key: 'twoOrMore', value: twoOrMoreVal.value },
  { key: 'undefined', value: declinedVal.value + unknownVal.value },
  { key: 'nativeAmerican', value: nativeAmericanVal.value },
  { key: 'asian', value: asianVal.value },
  { key: 'hispanicLatino', value: hispanicLatinoVal.value },
  { key: 'white', value: whiteVal.value },
  { key: 'pacificIslander', value: pacificIslanderVal.value },
].filter((item) => item.value > 0).sort((a, b) => b.value - a.value));

const plugins = ref([
  ChartDataLabels,
]);

const chartData = computed(() => {
  const datasets = [
    {
      label: 'Black or African American',
      axisLabel: ['Black or African', 'American'],
      backgroundColor: candidateBlack.value,
      borderWidth: 0,
      borderRadius: 4,
      value: blackVal.value,
      count: black,
      sortKey: 'black',
      maxBarThickness: 95,
    },
    {
      label: 'Two or More Races',
      axisLabel: 'Two or More Races',
      backgroundColor: candidateTwoOrMore.value,
      borderWidth: 0,
      borderRadius: 4,
      value: twoOrMoreVal.value,
      count: twoOrMore,
      sortKey: 'twoOrMore',
      maxBarThickness: 95,
    },
    {
      label: 'Declined',
      axisLabel: 'Undefined',
      backgroundColor: candidateDeclined.value,
      borderWidth: 0,
      borderRadius: 4,
      value: declinedVal.value,
      count: declined,
      sortKey: 'undefined',
      maxBarThickness: 95,
    },
    {
      label: 'Not Submitted',
      axisLabel: 'Undefined',
      backgroundColor: candidateNotSubmitted.value,
      borderWidth: 0,
      borderRadius: 4,
      value: unknownVal.value,
      count: unknown,
      sortKey: 'undefined',
      maxBarThickness: 95,
    },
    {
      label: 'American Indian or Alaska Native',
      axisLabel: ['American Indian', 'or Alaska Native'],
      backgroundColor: candidateNativeAm.value,
      borderWidth: 0,
      borderRadius: 4,
      value: nativeAmericanVal.value,
      count: nativeAmerican,
      sortKey: 'nativeAmerican',
      maxBarThickness: 95,
    },
    {
      label: 'Asian',
      axisLabel: 'Asian',
      backgroundColor: candidateAsian.value,
      borderWidth: 0,
      borderRadius: 4,
      value: asianVal.value,
      count: asian,
      sortKey: 'asian',
      maxBarThickness: 95,
    },
    {
      label: 'Hispanic or Latino',
      axisLabel: 'Hispanic or Latino',
      backgroundColor: candidateHispanic.value,
      borderWidth: 0,
      borderRadius: 4,
      value: hispanicLatinoVal.value,
      count: hispanicLatino,
      sortKey: 'hispanicLatino',
      maxBarThickness: 95,
    },
    {
      label: 'White',
      axisLabel: 'White',
      backgroundColor: candidateWhite.value,
      borderWidth: 0,
      borderRadius: 4,
      value: whiteVal.value,
      count: white,
      sortKey: 'white',
      maxBarThickness: 95,
    },
    {
      label: 'Native Hawaiian or Other Pacific Islander',
      axisLabel: ['Native Hawaiian or', 'Other Pacific Islander'],
      backgroundColor: candidateHawaiian.value,
      borderWidth: 0,
      borderRadius: 4,
      value: pacificIslanderVal.value,
      count: pacificIslander,
      sortKey: 'pacificIslander',
      maxBarThickness: 95,
    },
  ].filter((dataset) => dataset.value > 0).sort((a, b) => {
    const aIndex = sortArray.value.findIndex((item) => item.key === a.sortKey);
    const bIndex = sortArray.value.findIndex((item) => item.key === b.sortKey);
    return aIndex - bIndex;
  });

  datasets.forEach((dataset) => {
    const sortIndex = sortArray.value.findIndex((item) => item.key === dataset.sortKey);
    dataset.data = Array(sortArray.value.length).fill(0); // eslint-disable-line no-param-reassign
    dataset.data[sortIndex] = dataset.value; // eslint-disable-line no-param-reassign
  });

  const labels = datasets.map((dataset) => {
    if (dataset.label === 'Declined') return '';
    return dataset.axisLabel;
  }).filter(Boolean);

  return {
    datasets,
    labels,
  };
});

const suggestedMax = computed(() => {
  const max = Math.max(
    blackVal.value,
    twoOrMoreVal.value,
    declinedVal.value + unknownVal.value,
    nativeAmericanVal.value,
    asianVal.value,
    hispanicLatinoVal.value,
    whiteVal.value,
    pacificIslanderVal.value,
  );

  return Math.ceil(max / 10) * 10;
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  scales: {
    x: {
      beginAtZero: true,
      suggestedMax: suggestedMax.value,
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        callback: (value) => `${value}%`,
        color: candidateNotSubmitted.value,
        font: {
          size: 10,
        },
        maxTicksLimit: suggestedMax.value / 10 + 1,
      },
    },
    y: {
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        font: {
          size: 11,
          weight: 500,
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    datalabels: datalabelsConfig(textDarkest, whiteColor, suggestedMax),
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: (context) => initTooltip({
        context,
        tooltipId: 'racial-distribution-tooltip',
        createLineItems: (tooltip) => flatten(tooltip.dataPoints.map((point) => [
          { value: `Percentage: ${point.dataset.value.toFixed(1)}%` },
          { value: `Count: ${numberWithCommas(point.dataset.count || 0)}` },
        ])),
        createTitle: (tooltip) => tooltip.dataPoints[0].dataset.label,
      }),
    },
  },
}));
</script>

<style lang="less" scoped>
.chart-container {
  padding: 2px 16px 5px 10px;
  height: 320px;
  width: 100%;
}
</style>
